import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getDiscardedCardsV2HousesHouseIdMaintenanceDiscardedGetInfiniteOptions } from '@/client/@tanstack/react-query.gen';
import type { PlanNodeOut } from '@/client';
import { planNodeForDisplay } from '@/components/maintenance/model/planSelectors';

const NO_CARDS: PlanNodeOut[] = [];
export const DISCARDED_PAGE_LIMIT = 100;

/**
 * Reads discarded cards a page at a time.
 *
 * The first page carries the full count for the archive picker, so the screen can draw immediately
 * and fetch the growing restore list only when the reader asks for more.
 */
export const useDiscardedCards = (houseId: number | undefined) => {
	const result = useInfiniteQuery({
		...getDiscardedCardsV2HousesHouseIdMaintenanceDiscardedGetInfiniteOptions({
			path: { house_id: houseId as number },
			query: { limit: DISCARDED_PAGE_LIMIT },
		}),
		initialPageParam: 0,
		getNextPageParam: (lastPage) => {
			if (!lastPage.page_info.has_more || lastPage.page_info.limit <= 0) return undefined;
			return lastPage.page_info.offset + lastPage.page_info.limit;
		},
		enabled: houseId !== undefined,
	});
	const cards = useMemo(() => {
		if (!result.data) return NO_CARDS;

		const collected: PlanNodeOut[] = [];
		const seenNodeIds = new Set<string>();
		for (const page of result.data.pages) {
			for (const card of page.cards ?? []) {
				if (seenNodeIds.has(card.node_id)) continue;
				seenNodeIds.add(card.node_id);
				// The archive uses the same corrected titles and cadence wording as the live plan.
				collected.push(planNodeForDisplay(card));
			}
		}
		return collected;
	}, [result.data]);
	const totalCount = result.data?.pages[0]?.page_info.total_count ?? 0;

	return { ...result, cards, totalCount };
};
